import { PARAMS } from '../params';
import { AuthResponse, SignInRequest, UserLoginRequest } from '../types/auth';
import authConfig from '../config/domains';
import {
  isLocalTarget,
  preserveRedirectInPath,
  buildFinalRedirect,
} from '../utils/redirect';

// Re-export for backwards compat — external consumers (legal-entity-selection)
// imported isLocalTarget from this file in PR-3a.
export { isLocalTarget };

const headers = {
  'Accept': 'application/json, text/plain, */*',
  'Content-Type': 'application/json',
  'Authorization': 'Bearer',
  'source': 'web'
};

// API endpoints
const userLoginAPI = `${PARAMS.API_BASE_URL}/user_management/user_login`;
const userSignInAPI = `${PARAMS.API_BASE_URL}/signin`;
const verifyCodeAPI = `${PARAMS.API_BASE_URL}/user_management/verify_code`;
const createPasswordAPI = `${PARAMS.API_BASE_URL}/user_management/user_update_password`;
const resetCodeAPI = `${PARAMS.API_BASE_URL}/user_management/resend_code`;
const forgotPasswordAPI = `${PARAMS.API_BASE_URL}/user_management/forgot_password`;
const updatePasswordAPI = `${PARAMS.API_BASE_URL}/user_management/user_update_password`;
const staffingSelectionAPI = `${PARAMS.API_BASE_URL}/staffing-selection`;

// Helper function to set cookies with appropriate domain
const setCookie = (name: string, value: string, days = 7) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  
  // Determine if we're on localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // For localhost, we don't need domain or secure flag
  if (isLocalhost) {
    document.cookie = `${name}=${value};${expires};path=/`;
  } else {
    // For HTTPS domains: Secure + SameSite=Lax. Every Veytan surface is the
    // SAME SITE (registrable domain veytan.com), so Lax still rides the
    // cross-repo redirect (auth -> hcm / payroll). Lax (not None) so no
    // third-party site can attach this cookie — that closes the CSRF surface
    // and the staging<->prod cross-env cookie replay (shared Cognito pool).
    // NOTE: this helper is currently UNUSED (real sessions are HttpOnly
    // cookies set by the BE — see signIn); kept as a safety net for the
    // cross-repo redirect. Do NOT widen back to None.
    console.log('domain variable---', isLocalhost, name, value, expires, authConfig.getCookieDomain());
    document.cookie = `${name}=${value};${expires};path=/;domain=${authConfig.getCookieDomain()};secure;SameSite=Lax`;
  }
  
  // Also store in localStorage for additional access
  try {
    localStorage.setItem(name, value);
  } catch (e) {
    console.error('Error storing in localStorage:', e);
  }
};

// Helper function to get cookie value by name
export const getCookie = (name: string): string | null => {
  console.log('Getting cookie for:', name);
  console.log('Current hostname:', window.location.hostname);
  console.log('All cookies:', document.cookie);
  const cookieName = `${name}=`;
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return null;
};

// Helper function to get value by name (checks both cookie and localStorage)
const getValue = (name: string): string | null => {
  // First try to get from cookie
  const cookieValue = getCookie(name);
  if (cookieValue) return cookieValue;
  
  // If not in cookie, try localStorage
  try {
    return localStorage.getItem(name);
  } catch (e) {
    console.error('Error reading from localStorage:', e);
    return null;
  }
};

// Helper function to delete both cookie and localStorage item
const deleteValue = (name: string) => {
  // Determine if we're on localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Delete from cookie
  const cookieString = isLocalhost
    ? `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    : `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${authConfig.getCookieDomain()};secure`;
    
  document.cookie = cookieString;
  
  // Delete from localStorage
  try {
    localStorage.removeItem(name);
  } catch (e) {
    console.error('Error removing from localStorage:', e);
  }
};

export const authService = {
  validateEmail: async (data: UserLoginRequest): Promise<boolean> => {
    try {
      const response = await fetch(`${PARAMS.API_BASE_URL}/user_management/user_login`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error('Email validation failed');
      }
      
      return true;
    } catch (error) {
      console.error('Email validation error:', error);
      return false;
    }
  },
  
  signIn: async (data: SignInRequest): Promise<AuthResponse | null> => {
    try {
      const response = await fetch(`${PARAMS.API_BASE_URL}/signin`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Sign in failed');
      }

      const authData: AuthResponse = await response.json();

      // Tokens are stored as HttpOnly cookies on .veytan.com by the BE — no JS cookie writes here.
      // userinfo can stay in localStorage for in-app convenience (it's not a credential).
      localStorage.setItem(PARAMS.USER_STORAGE_KEY, JSON.stringify(authData.userinfo));

      return authData;
    } catch (error) {
      console.error('Sign in error:', error);
      return null;
    }
  },
  
  isAuthenticated: (): boolean => {
    // Post HttpOnly-cookie migration the JWT lives in the BE-set HttpOnly
    // cookie on .veytan.com — JS cannot read it. The signin() function above
    // explicitly notes: "Tokens are stored as HttpOnly cookies on .veytan.com
    // by the BE — no JS cookie writes here." So `getValue(TOKEN_STORAGE_KEY)`
    // returns null for every signed-in user on deployed, making this function
    // structurally always return false → `ProtectedRoute` redirects every
    // protected page to /signin-new.
    //
    // userinfo is the only thing we intentionally persist to JS storage
    // (it's not a credential) and it's only written by signin() after a
    // successful authentication. Its presence is therefore the correct
    // structural proof of session post-migration.
    return !!getValue(PARAMS.USER_STORAGE_KEY);
  },
  
  getToken: (): string | null => {
    return getValue(PARAMS.TOKEN_STORAGE_KEY);
  },
  
  getUser: (): any => {
    const userJson = getValue(PARAMS.USER_STORAGE_KEY);
    if (userJson) {
      try {
        return JSON.parse(userJson);
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    return null;
  },
  
  logout: async (): Promise<void> => {
    // 1. Tell the BE to revoke the Cognito refresh token + flush the Redis
    //    session cache + clear the HttpOnly cookies via Set-Cookie. The BE
    //    endpoint is in excludedRoutes, so it works even with expired tokens.
    //    credentials: 'include' lets HttpOnly cookies travel to the BE.
    try {
      await fetch(`${PARAMS.API_BASE_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.warn('BE logout failed; proceeding with client-side cleanup', e);
    }

    // 2. Belt-and-suspenders client-side cleanup for any JS-visible cookies.
    //    BE-set HttpOnly cookies were cleared by Set-Cookie in step 1.
    //    `removeAllJsCookies` clears the full JS-visible cookie set on the
    //    current origin (handles any localhost JS-Bearer mirror that the
    //    enumerated-name deletes below could miss). Then the named deletes
    //    cover any cookies that need a specific Domain attribute to match
    //    the original Set-Cookie's scope.
    removeAllJsCookies();
    deleteValue(PARAMS.TOKEN_STORAGE_KEY);
    deleteValue(PARAMS.TOKEN_STORAGE_KEY_REFRESH);
    deleteValue(PARAMS.TOKEN_STORAGE_KEY_ID);
    deleteValue('veytan_auth_workspace');
    localStorage.removeItem(PARAMS.USER_STORAGE_KEY);
  }
};

/**
 * Clear every JS-visible cookie on the current origin. Used by logout() to
 * catch any cookie that the enumerated-name `deleteValue(...)` calls below
 * would miss (notably the localhost-only JS Bearer mirror that's written
 * by signin's `?token=` handoff but isn't in PARAMS).
 *
 * Limitations:
 *   - Cannot enumerate HttpOnly cookies (JS doesn't see them in document.cookie).
 *     Those are cleared server-side by the BE's /logout endpoint.
 *   - Clears without specifying Domain — so a cookie scoped to `.veytan.com`
 *     won't be cleared by this (browsers require Domain to match the original
 *     Set-Cookie). The named `deleteValue` calls below use authConfig.getCookieDomain()
 *     to handle that case.
 */
const removeAllJsCookies = () => {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    if (name) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  }
};

// New API functions converted from callAPI to fetch

export const getLoggedInUserData = async (data: any) => {
  try {
    const response = await fetch(userLoginAPI, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'An error occurred',
        status: response.status
      };
    }
    
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return {
      error: true,
      message: 'Network error. Please check your connection.'
    };
  }
};

export const userSignIn = async (data: any) => {
  try {
    const response = await fetch(userSignInAPI, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'An error occurred',
        status: response.status
      };
    }
    
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return {
      error: true,
      message: 'Network error. Please check your connection.'
    };
  }
};

export const verifyCode = async (data: any) => {
  try {
    const response = await fetch(verifyCodeAPI, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'An error occurred',
        status: response.status
      };
    }
    
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return {
      error: true,
      message: 'Network error. Please check your connection.'
    };
  }
};

export const createPassword = async (data: any) => {
  try {
    const response = await fetch(createPasswordAPI, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'An error occurred',
        status: response.status
      };
    }
    
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return {
      error: true,
      message: 'Network error. Please check your connection.'
    };
  }
};

export const resendCode = async (data: any) => {
  try {
    const response = await fetch(resetCodeAPI, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'An error occurred',
        status: response.status
      };
    }
    
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return {
      error: true,
      message: 'Network error. Please check your connection.'
    };
  }
};

export const forgotPassword = async (data: any) => {
  try {
    const response = await fetch(forgotPasswordAPI, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'An error occurred',
        status: response.status
      };
    }
    
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return {
      error: true,
      message: 'Network error. Please check your connection.'
    };
  }
};

export const updatePassword = async (data: any) => {
  try {
    const response = await fetch(updatePasswordAPI, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'An error occurred',
        status: response.status
      };
    }
    
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return {
      error: true,
      message: 'Network error. Please check your connection.'
    };
  }
};

export const getParentCorpLegalEntities = async (email: string, corporation_id: number) => {
  try {
    const token = authService.getToken();
    const response = await fetch(`${PARAMS.API_BASE_URL}/parent-corp-legal-entity`, {
      method: 'POST',
      headers: {
        ...headers,
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({ email, corporation_id })
    });
    if (!response.ok) {
      const errorData = await response.json();
      return { error: true, message: errorData.message || 'An error occurred' };
    }
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return { error: true, message: 'Network error. Please check your connection.' };
  }
};

export const staffingSelection = async (data: { email: string; corporation_id: number; legal_entity_id?: number }) => {
  try {
    const response = await fetch(staffingSelectionAPI, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'source': 'web'
      },
      credentials: 'include',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'An error occurred',
        status: response.status
      };
    }
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return {
      error: true,
      message: 'Network error. Please check your connection.'
    };
  }
};

export const getSvcCategoryProducts = async (svcCatCode: string) => {
  try {
    const token = authService.getToken();
    const response = await fetch(
      `${PARAMS.API_BASE_URL}/svc-category/products?svc_cat_code=${svcCatCode}`,
      {
        method: 'GET',
        headers: {
          ...headers,
          'Authorization': `Bearer ${token}`
        }
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      return { error: true, message: errorData.message || 'An error occurred' };
    }
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return { error: true, message: 'Network error. Please check your connection.' };
  }
};

export const getClients = async (svcCatId: string, userId: string, parentCorpId?: string | number) => {
  try {
    const token = authService.getToken();
    const customHeaders = {
      ...headers,
      'Authorization': `Bearer ${token}`
    };

    let url = `${PARAMS.API_BASE_URL}/organizationsetup/clients?svc_cat_id=${svcCatId}&user_id=${userId}`;
    if (parentCorpId) url += `&parent_corp_id=${parentCorpId}`;

    const response = await fetch(
      url,
      {
        method: 'GET',
        headers: customHeaders,
        credentials: 'include',
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'An error occurred',
        status: response.status
      };
    }
    
    return await response.json();
  } catch (error) {
    console.error('API error:', error);
    return {
      error: true,
      message: 'Network error. Please check your connection.'
    };
  }
};

// New service functions to handle operations from signin-new/page.tsx

// Service function for checking if a user is new
export const checkUserStatus = async (formData: any) => {
  try {
    const response = await getLoggedInUserData(formData);
    
    return response;
  } catch (error) {
    console.error('Error checking user status:', error);
    return {
      error: true,
      message: 'Failed to check user status'
    };
  }
};

// Service function for handling user login with token storage
export const handleUserLogin = async (values: any, dispatch: any, setData: any, navigate: any, location: any = null) => {
  try {
    const response = await userSignIn(values);
    let finalResponse = response;
    if (response && !response.error) {
      console.log('Login successful, storing tokens...');
      // New flow: handle corporation_details
      finalResponse = response;
if (response.corporation_details?.length > 1) {
        // Multiple corps → tokens are already stored as HttpOnly cookies by BE on /signin.
        // Email must come from the /signin response (userinfo.email), not the
        // user-typed form value — downstream screens (corp-selection,
        // legal-entity-selection) forward this email in their API payloads.
        dispatch(setData({
          data: { corporation_details: response.corporation_details, email: response.userinfo?.email || response.email || values.email },
          accesstoken: response.accesstoken,
          refreshtoken: response.refreshtoken
        }));
        const params = location ? new URLSearchParams(location.search) : null;
        const redirectParam = params?.get('redirect');
        const corpPath = redirectParam ? `/corp-selection?redirect=${encodeURIComponent(redirectParam)}` : '/corp-selection';
        navigate(corpPath);
        return response;
      } else if (response.corporation_details?.length === 1) {
        // Single corp → tokens are already stored as HttpOnly cookies by BE on /signin.
        dispatch(setData({
          data: {
            corporation_details: response.corporation_details,
            selected_corp: response.corporation_details[0],
            // Email from /signin response (userinfo.email), not the user-typed
            // form value — see multi-corp branch above for why.
            email: response.userinfo?.email || response.email || values.email
          },
          accesstoken: response.accesstoken,
          refreshtoken: response.refreshtoken
        }));
        const params = location ? new URLSearchParams(location.search) : null;
        const redirectParam = params?.get('redirect');
        const lePath = redirectParam ? `/legal-entity-selection?redirect=${encodeURIComponent(redirectParam)}` : '/legal-entity-selection';
        navigate(lePath);
        return response;
      }

      // Tokens are stored as HttpOnly cookies on .veytan.com by the BE on /signin.
      localStorage.setItem(PARAMS.USER_STORAGE_KEY, JSON.stringify(finalResponse.userinfo));

      // Store data in Redux
      dispatch(setData({
        data: { userinfo: finalResponse.userinfo },
        accesstoken: finalResponse.accesstoken,
        refreshtoken: finalResponse.refreshtoken
      }));

      // Mid-chain navigation to /workspace-selection: just preserve `?redirect=`,
      // don't append the token here. Token append happens at the FINAL hop
      // (buildFinalRedirect in workspace-selection's terminal redirect).
      // This avoids leaking the token through intermediate URL bars / history.
      const preserveRedirect = (targetPath: string) =>
        location ? preserveRedirectInPath(targetPath, location) : targetPath;

      // Check if workspace selection is needed
      const staffingDetails = finalResponse.userinfo?.staffing_corp_access_list?.staffing_details || [];
      const parentCorpData = finalResponse.userinfo?.parentCorpData;
      if (staffingDetails.length > 0) {
        // Has staffing list → workspace selection needed
        navigate(preserveRedirect('/workspace-selection'));
      } else if (parentCorpData) {
        // Only parent corp, no staffing list → bypass workspace-selection, redirect directly
        const updatedUserInfo = {
          ...finalResponse.userinfo,
          corporation_id: parentCorpData.id,
          parent_corp_id: parentCorpData.id,
          selected_client: {
            clt_corporation_details: {
              id: parentCorpData.id,
              full_name: parentCorpData.full_name,
              logo_url: Array.isArray(parentCorpData.logo_url)
                ? parentCorpData.logo_url[0]?.presigned_url || null
                : parentCorpData.logo_url || null
            }
          }
        };
        localStorage.setItem(PARAMS.USER_STORAGE_KEY, JSON.stringify(updatedUserInfo));
        dispatch(setData({
          data: { userinfo: updatedUserInfo },
          accesstoken: finalResponse.accesstoken,
          refreshtoken: finalResponse.refreshtoken
        }));

        const wsData = {
          workspace: { type: 'corporation', corporation_ids: [], legal_entity_ids: [], svc_cat_id: '', svc_cat_details: {} },
          client: {
            clt_corporation_details: {
              id: parentCorpData.id,
              full_name: parentCorpData.full_name,
              logo_url: Array.isArray(parentCorpData.logo_url)
                ? parentCorpData.logo_url[0]?.presigned_url || null
                : parentCorpData.logo_url || null
            }
          },
          corporation_id: parentCorpData.id,
          parent_corp_id: parentCorpData.id,
          legal_entity_id: '',
          svc_cat_id: ''
        };

        const queryParams = location ? new URLSearchParams(location.search) : null;
        const baseRedirect = queryParams?.get('redirect') || PARAMS.DEFAULT_REDIRECT_URL;
        const token = finalResponse.accesstoken || getCookie(PARAMS.TOKEN_STORAGE_KEY);
        window.location.href = buildFinalRedirect(baseRedirect, {
          token: token || undefined,
          workspaceData: wsData,
        });
      } else {
        // No workspace selection needed
        let taskStatus = finalResponse.userinfo?.task || 'NA';
        if (taskStatus == 'pending') {
          navigate('/home');
        } else {
          navigate('/home/dashboard');
        }
      }
    }

    return finalResponse;
  } catch (error) {
    console.error('Error during login:', error);
    return {
      error: true,
      message: 'Failed to log in'
    };
  }
};

// Service function for verifying code
export const handleVerifyCode = async (value: any, email: string, isForgotPassword: boolean, navigate: any) => {
  try {
    const response = await verifyCode(value);
    
    if (response.success) {
      navigate(`/signin-new/password?id=${encodeURIComponent(email)}&forgot=${isForgotPassword}`);
    }
    
    return response;
  } catch (error) {
    console.error('Error verifying code:', error);
    return {
      error: true,
      message: 'Failed to verify code'
    };
  }
};

// Persist the user's workspace selection to the backend.
// The BE sets `veytan_auth_workspace` (HttpOnly, Domain=.veytan.com) so that subsequent
// /decodeToken calls on hrms-01 can merge workspace context (selected_client,
// selected_workspace, parent_corp_id, parent_legal_entity_id, etc.) into the userinfo
// response. Without this call, hrms-01's Redux is missing client + workspace details
// and downstream UI (Client organisation name, Switch view bar, multi-tenant API
// query params) breaks.
export const setWorkspace = async (workspaceData: any) => {
  try {
    const response = await fetch(`${PARAMS.API_BASE_URL}/set-workspace`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(workspaceData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      return {
        error: true,
        message: errorData.message || 'Failed to set workspace',
        status: response.status,
      };
    }
    return await response.json();
  } catch (error) {
    console.error('setWorkspace error:', error);
    return {
      error: true,
      message: 'Network error while setting workspace',
    };
  }
};

// Service function to update user information after workspace/client selection
export const updateUserWorkspace = async (userInfo: any, workspaceData: any, isClient: boolean = false, clientData: any = null) => {
  try {
    let updatedUserInfo;
    
    if (isClient) {
      // Client selection
      updatedUserInfo = {
        ...userInfo,
        selected_workspace: workspaceData,
        selected_client: clientData,
        corporation_id: clientData.clt_corporation_details?.id,
        parent_corp_id: userInfo.parent_corp_id || null,
        legal_entity_id: clientData.clt_legal_entity_details?.id,
        svc_cat_id: clientData.svc_cat_id
      };
    } else {
      // Workspace selection (corporation / parent corp)
      updatedUserInfo = {
        ...userInfo,
        selected_workspace: workspaceData,
        corporation_id: workspaceData.id,
        parent_corp_id: workspaceData.id,
        selected_client: {
          clt_corporation_details: {
            id: workspaceData.id,
            full_name: workspaceData.full_name,
            logo_url: Array.isArray(workspaceData.logo_url)
              ? workspaceData.logo_url[0]?.presigned_url || null
              : workspaceData.logo_url || null
          }
        }
      };
    }

    // Get current tokens
    const token = authService.getToken();
    const refreshToken = getValue(PARAMS.TOKEN_STORAGE_KEY_REFRESH);
    
    // Update user info in localStorage only, not as a cookie
    localStorage.setItem(PARAMS.USER_STORAGE_KEY, JSON.stringify(updatedUserInfo));
    
    // Return updated user info and tokens
    return {
      userInfo: updatedUserInfo,
      token,
      refreshToken
    };
  } catch (error) {
    console.error('Error updating user workspace:', error);
    return {
      error: true,
      message: 'Failed to update user workspace information'
    };
  }
};

// Helper function to determine redirect path based on task status
export const getRedirectPath = (userInfo: any) => {
  const taskStatus = userInfo?.task || 'NA';
  return taskStatus === 'pending' ? '/home' : '/home/dashboard';
}; 
